/**
 * "Reminder" on a request card, and the create/edit modal both entry points share.
 *
 * Manual only, by decision (Sofia, Aug 2026): the recruiter clicks to create a reminder — nothing
 * is auto-created from sent replies. Prefilled from the card (candidate + subject) so the usual
 * case is "set the deadline, save"; free-standing reminders come from the strip's own add button
 * with no prefill.
 *
 * The title usually names a candidate: it goes to Firestore (6-month retention) and on screen,
 * never into analytics — useReminders owns the events and sends categories only.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { ModalActions } from './ModalActions';
import { useReminders } from './useReminders';

/** Local wall-clock `YYYY-MM-DDTHH:mm` for <input type="datetime-local">. */
function toLocalInputValue(date: Date): string {
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Tomorrow 09:00 local — the deadline a follow-up usually means. */
export function defaultDueAt(now = new Date()): string {
  const due = new Date(now);
  due.setDate(due.getDate() + 1);
  due.setHours(9, 0, 0, 0);
  return toLocalInputValue(due);
}

export function ReminderModal({
  open,
  onClose,
  prefill,
  edit,
}: {
  open: boolean;
  onClose: () => void;
  /** Create mode: what the title box starts with, and which thread the reminder points back to. */
  prefill?: { title?: string; threadId?: string | null; candidateName?: string | null };
  /** Edit mode: the existing reminder's id + current values. */
  edit?: { id: string; title: string; dueAt: string };
}) {
  const { create, update } = useReminders();
  const [title, setTitle] = useState(edit?.title ?? prefill?.title ?? '');
  const [dueAt, setDueAt] = useState(
    edit ? toLocalInputValue(new Date(edit.dueAt)) : defaultDueAt()
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!title.trim() || !dueAt) return;
    setSaving(true);
    setError(null);
    try {
      const dueIso = new Date(dueAt).toISOString();
      if (edit) await update(edit.id, { title: title.trim(), dueAt: dueIso });
      else {
        await create({
          title: title.trim(),
          dueAt: dueIso,
          threadId: prefill?.threadId ?? null,
          candidateName: prefill?.candidateName ?? null,
        });
      }
      showToast({ status: 'success', description: 'Reminder saved.' });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the reminder');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={(next: boolean) => !next && onClose()}>
      <ModalContent className="!h-max !w-[min(100vw-2rem,32rem)] gap-4 rounded p-6" data-no-toggle>
        <ModalHeader>
          <ModalTitle>{edit ? 'Edit reminder' : 'Create a reminder'}</ModalTitle>
        </ModalHeader>
        <div className="space-y-3">
          <Input
            label="What needs doing?"
            value={title}
            onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
          />
          {/* Native picker — the repo's precedent for date+time (InterviewRescheduleButton). */}
          <div className="space-y-1.5">
            <label htmlFor="ta-reminder-due" className="block text-sm font-medium text-gray-700">
              Deadline
            </label>
            <input
              id="ta-reminder-due"
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          {error && <p className="text-sm text-red-700">{error}</p>}
          <p className="text-xs text-gray-500">
            Shown at the top of the Requests tab until you tick it off. Only you see your reminders.
          </p>
        </div>
        <ModalActions
          confirmLabel={saving ? 'Saving…' : 'Save reminder'}
          confirmDisabled={saving || !title.trim() || !dueAt}
          onConfirm={() => void save()}
          onCancel={onClose}
        />
      </ModalContent>
    </Modal>
  );
}

/** The card's entry point. Renders inside containers that already carry data-no-toggle. */
export function CreateReminderButton({
  threadId,
  candidateName,
  subject,
}: {
  threadId: string;
  candidateName: string;
  subject: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-no-toggle>
        Reminder
      </Button>
      {open && (
        <ReminderModal
          open
          onClose={() => setOpen(false)}
          prefill={{
            title: `Follow up with ${candidateName} — ${subject}`,
            threadId,
            candidateName,
          }}
        />
      )}
    </>
  );
}
